import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';

import {
  getCompletionReport,
  getOverdueReport,
  getComplianceReport,
  getQuizStatsReport,
  getRetryAnalysisReport,
  getFailureAnalysisReport,
  getCertificationReport,
  getBadgeReport,
  getEvaluationReport,
  getVersionHistoryReport,
  exportTrainingReport,
} from '../../services/trainingReportService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Tabs from '../../components/ui/Tabs';
import DataTable from '../../components/ui/DataTable';
import { parseTableParams, formatDate } from '../../utils/Utils';
import PersonCell from '../../components/ui/PersonCell';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $s }) => {
    if ($s === 'completed' || $s === 'valid') return '#D1FAE5';
    if ($s === 'in_progress' || $s === 'expiring') return '#FEF3C7';
    if ($s === 'failed' || $s === 'expired') return '#FEE2E2';
    if ($s === 'not_started' || $s === 'assigned') return '#E0E7FF';
    return '#F1F5F9';
  }};
  color: ${({ $s }) => {
    if ($s === 'completed' || $s === 'valid') return '#065F46';
    if ($s === 'in_progress' || $s === 'expiring') return '#92400E';
    if ($s === 'failed' || $s === 'expired') return '#991B1B';
    if ($s === 'not_started' || $s === 'assigned') return '#3730A3';
    return '#475569';
  }};
`;

const RateCell = ({ value }) => {
  const color = value >= 80 ? '#16A34A' : value >= 50 ? '#F59E0B' : '#EF4444';
  return <span style={{ color, fontWeight: 700 }}>%{value}</span>;
};

// Each tab's report config: slug, label, fetch fn, columns
const useReportConfigs = (t, lang, statusLabel) => useMemo(() => [
  {
    slug: 'completion',
    label: t('trainingReports.completion'),
    fetch: getCompletionReport,
    columns: [
      { name: 'title', label: t('training.title'), options: { filter: false, sort: true } },
      { name: 'categoryName', label: t('training.category'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'isMandatory', label: t('training.mandatory'), options: { filter: false, sort: false, customBodyRender: (v) => v ? t('common.yes') : t('common.no') } },
      { name: 'assignedCount', label: t('trainingReports.assigned'), options: { filter: false, sort: true } },
      { name: 'completedCount', label: t('trainingReports.completed'), options: { filter: false, sort: true } },
      { name: 'inProgressCount', label: t('trainingReports.inProgress'), options: { filter: false, sort: false } },
      { name: 'failedCount', label: t('trainingReports.failed'), options: { filter: false, sort: false } },
      { name: 'completionRate', label: t('trainingReports.completionRate'), options: { filter: false, sort: true, customBodyRender: (v) => <RateCell value={v} /> } },
      { name: 'averageCompletionDays', label: t('trainingReports.avgCompletionDays'), options: { filter: false, sort: false, customBodyRender: (v) => v != null ? v : '—' } },
    ],
  },
  {
    slug: 'overdue',
    label: t('trainingReports.overdue'),
    fetch: getOverdueReport,
    columns: [
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'trainingTitle', label: t('training.title'), options: { filter: false, sort: false } },
      { name: 'dueDate', label: t('training.dueDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
      { name: 'overdueDays', label: t('trainingReports.overdueDays'), options: { filter: false, sort: true, customBodyRender: (v) => <span style={{ color: v > 14 ? '#ef4444' : '#f59e0b', fontWeight: 700 }}>{v}</span> } },
      { name: 'isMandatory', label: t('training.mandatory'), options: { filter: false, sort: false, customBodyRender: (v) => v ? t('common.yes') : t('common.no') } },
      { name: 'status', label: t('training.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{statusLabel(v)}</StatusBadge> } },
    ],
  },
  {
    slug: 'compliance',
    label: t('trainingReports.compliance'),
    fetch: getComplianceReport,
    columns: [
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'requiredCount', label: t('trainingReports.required'), options: { filter: false, sort: true } },
      { name: 'completedCount', label: t('trainingReports.completed'), options: { filter: false, sort: false } },
      { name: 'missingCount', label: t('trainingReports.missing'), options: { filter: false, sort: true, customBodyRender: (v) => <span style={{ color: v > 0 ? '#ef4444' : '#16A34A', fontWeight: 700 }}>{v}</span> } },
      { name: 'complianceRate', label: t('trainingReports.complianceRate'), options: { filter: false, sort: true, customBodyRender: (v) => <RateCell value={v} /> } },
      { name: 'missingTrainings', label: t('trainingReports.missingTrainings'), options: { filter: false, sort: false, customBodyRender: (v) => (v || []).join(', ') || '—' } },
    ],
  },
  {
    slug: 'quiz-stats',
    label: t('trainingReports.quizStats'),
    fetch: getQuizStatsReport,
    columns: [
      { name: 'title', label: t('training.title'), options: { filter: false, sort: true } },
      { name: 'totalAttempts', label: t('trainingReports.totalAttempts'), options: { filter: false, sort: true } },
      { name: 'passedCount', label: t('trainingReports.passed'), options: { filter: false, sort: false } },
      { name: 'failedCount', label: t('trainingReports.failed'), options: { filter: false, sort: false } },
      { name: 'passRate', label: t('trainingReports.passRate'), options: { filter: false, sort: true, customBodyRender: (v) => <RateCell value={v} /> } },
      { name: 'averageScore', label: t('trainingReports.avgScore'), options: { filter: false, sort: true, customBodyRender: (v) => v != null ? `%${v}` : '—' } },
      { name: 'passingScore', label: t('trainingReports.passingScore'), options: { filter: false, sort: false, customBodyRender: (v) => `%${v}` } },
    ],
  },
  {
    slug: 'retry',
    label: t('trainingReports.retry'),
    fetch: getRetryAnalysisReport,
    columns: [
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'trainingTitle', label: t('training.title'), options: { filter: false, sort: false } },
      { name: 'attemptCount', label: t('trainingReports.attempts'), options: { filter: false, sort: true } },
      { name: 'maxAttempts', label: t('trainingReports.maxAttempts'), options: { filter: false, sort: false } },
      { name: 'bonusAttempts', label: t('trainingReports.bonusAttempts'), options: { filter: false, sort: false } },
      { name: 'passed', label: t('trainingReports.passed'), options: { filter: false, sort: false, customBodyRender: (v) => v === true ? <StatusBadge $s="completed">{t('common.yes')}</StatusBadge> : v === false ? <StatusBadge $s="failed">{t('common.no')}</StatusBadge> : '—' } },
      { name: 'bestScorePercent', label: t('trainingReports.bestScore'), options: { filter: false, sort: false, customBodyRender: (v) => v != null ? `%${v}` : '—' } },
    ],
  },
  {
    slug: 'failure',
    label: t('trainingReports.failure'),
    fetch: getFailureAnalysisReport,
    columns: [
      { name: 'trainingTitle', label: t('training.title'), options: { filter: false, sort: true } },
      { name: 'questionText', label: t('trainingReports.question'), options: { filter: false, sort: false } },
      { name: 'totalAnswered', label: t('trainingReports.answers'), options: { filter: false, sort: true } },
      { name: 'wrongCount', label: t('trainingReports.wrongCount'), options: { filter: false, sort: false } },
      { name: 'wrongRate', label: t('trainingReports.wrongRate'), options: { filter: false, sort: true, customBodyRender: (v) => <span style={{ color: v >= 50 ? '#ef4444' : v >= 30 ? '#f59e0b' : '#64748b', fontWeight: 700 }}>%{v}</span> } },
    ],
  },
  {
    slug: 'certifications',
    label: t('trainingReports.certifications'),
    fetch: getCertificationReport,
    columns: [
      { name: 'certificateNumber', label: t('training.certificateNumber'), options: { filter: false, sort: true } },
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'trainingTitle', label: t('training.title'), options: { filter: false, sort: false } },
      { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
      { name: 'issuedAt', label: t('training.certificateIssuedAt'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
      { name: 'expiresAt', label: t('training.certificateExpires'), options: { filter: false, sort: false, customBodyRender: (v) => v ? formatDate(v, lang) : '—' } },
      { name: 'status', label: t('training.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{t(`trainingReports.certStatus_${v}`)}</StatusBadge> } },
    ],
  },
  {
    slug: 'badges',
    label: t('trainingReports.badges'),
    fetch: getBadgeReport,
    columns: [
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'badgeCount', label: t('trainingReports.badgeCount'), options: { filter: false, sort: true } },
      { name: 'badgeNames', label: t('trainingReports.badges'), options: { filter: false, sort: false, customBodyRender: (v) => (v || []).join(', ') || '—' } },
      { name: 'lastEarnedAt', label: t('trainingReports.lastEarned'), options: { filter: false, sort: true, customBodyRender: (v) => v ? formatDate(v, lang) : '—' } },
    ],
  },
  {
    slug: 'evaluation',
    label: t('trainingReports.evaluation'),
    fetch: getEvaluationReport,
    columns: [
      { name: 'title', label: t('training.title'), options: { filter: false, sort: true } },
      { name: 'count', label: t('trainingReports.evalCount'), options: { filter: false, sort: true } },
      { name: 'averageRating', label: t('trainingReports.avgRating'), options: { filter: false, sort: true, customBodyRender: (v) => v != null ? `${v} / 5` : '—' } },
      { name: 'distribution', label: t('trainingReports.distribution'), options: { filter: false, sort: false, customBodyRender: (v) => (v || []).map(b => `${b.rating}★:${b.count}`).join(' ') || '—' } },
    ],
  },
  {
    slug: 'version-history',
    label: t('trainingReports.versionHistory'),
    fetch: getVersionHistoryReport,
    columns: [
      { name: 'userFullName', label: t('training.learner'), options: { personPhotoKey: 'userProfilePicture', filter: false, sort: true } },
      { name: 'title', label: t('training.title'), options: { filter: false, sort: false } },
      { name: 'versionLabel', label: t('training.version'), options: { filter: false, sort: false } },
      { name: 'status', label: t('training.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{statusLabel(v)}</StatusBadge> } },
      { name: 'quizPassed', label: t('trainingReports.quizPassed'), options: { filter: false, sort: false, customBodyRender: (v) => v === true ? t('common.yes') : v === false ? t('common.no') : '—' } },
      { name: 'bestScore', label: t('trainingReports.bestScore'), options: { filter: false, sort: false, customBodyRender: (v) => v != null ? v : '—' } },
      { name: 'assignedAt', label: t('trainingReports.assignedAt'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
      { name: 'completedAt', label: t('trainingReports.completedAt'), options: { filter: false, sort: false, customBodyRender: (v) => v ? formatDate(v, lang) : '—' } },
    ],
  },
], [t, lang, statusLabel]);


function ReportTable({ report }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [exporting, setExporting] = useState(false);

  // `personPhotoKey` taşıyan kolonlar avatarlı çizilir — rapor tanımları modül
  // düzeyinde (satır verisi kapsamda değil), bu yüzden render'ı tabloyu tutan
  // bileşen bağlar (kural: kullanıcı listelenen her yerde avatar).
  const columns = useMemo(() => report.columns.map((c) => {
    const key = c.options?.personPhotoKey;
    if (!key) return c;
    return {
      ...c,
      options: {
        ...c.options,
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.[key]} />
        ),
      },
    };
  }), [report.columns, data]);

  const fetchData = useCallback(async (params) => {
    setFetching(true);
    try {
      const res = await report.fetch(params);
      const result = res.data?.data;
      const items = result?.data || [];
      setData(items);
      setPagination({
        totalRowCount: result?.totalRowCount || 0,
        pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
        pageSize: result?.pageSize || 10,
      });
    } finally {
      setFetching(false);
    }
  }, [report]);

  React.useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleDataStateChange = useCallback((_, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, report.columns);
    setQueryParams(params);
  }, [report]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportTrainingReport(report.slug, { ...queryParams, pageSize: 10000, pageNumber: 1 });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
      a.download = match ? match[1].replaceAll('"', '') : `${report.slug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [queryParams, report.slug]);

  return (
    <DataTable
      title={report.label}
      fetching={fetching}
      columns={columns}
      data={data}
      totalRowCount={pagination.totalRowCount}
      pageIndex={pagination.pageIndex}
      pageSize={pagination.pageSize}
      filterList={filterList}
      onFilterConfirm={handleFilterConfirm}
      onTableChanged={handleDataStateChange}
      disableFilters
      toolbarActions={(
        <Button type="button" variant="outline" loading={exporting} disabled={data.length === 0} onClick={handleExport}>
          <Download size={16} aria-hidden="true" />
          {t('trainingReports.exportExcel')}
        </Button>
      )}
    />
  );
}


export default function TrainingReportsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const statusLabel = useCallback((s) => t(`training.assignmentStatus.${s}`, s), [t]);
  const reports = useReportConfigs(t, i18n.language, statusLabel);

  // Tab can be pre-selected via ?tab=overdue from a KPI card click.
  const initialTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const tab = qs.get('tab');
    return reports.find(r => r.slug === tab)?.slug || reports[0].slug;
  }, [location.search, reports]);

  const [activeTab, setActiveTab] = useState(initialTab);

  const tabs = reports.map(r => ({ key: r.slug, label: r.label }));
  const active = reports.find(r => r.slug === activeTab) || reports[0];

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[{ label: t('menu.training') }, { label: t('menu.trainingReports') }]} />
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        <ReportTable key={active.slug} report={active} />
      </TabContent>
    </PageContainer>
  );
}
